import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectCurrentInbox,
  selectCurrentBillingCycle,
  selectCurrentKb,
  selectPlans,
  selectIsSubscriptionActive,
  selectUpdateSubscriptionRequest,
  selectIsTrial,
  selectPrimaryCreditCard,
  selectIsBillingEstimateAvailable,
  selectShowCardErrorModal,
  selectIsUpdatingCard,
  selectIsCanceling,
  selectHasPlans,
  selectIsBillingDataLoaded,
  selectIsBillingDataLoading,
  selectBillingPendingSubscription,
  selectBillingProvider,
  selectBillingProviderSettings,
  selectBillingExternalAccountId,
  selectBillingExternalSubscriptionId,
  selectHasCompletedTrial,
  selectBillingStartTrialDurationDays,
  selectDaysLeftBeforeExpiring,
  selectHasPendingDowngrade,
  selectAvailablePlansByProduct,
  selectCurrentPlanByProduct,
  selectIsPlanLegacy,
  selectBillingEstimateForPlanChange,
  selectShowKbPlans,
  selectIsFreemium,
} from 'ducks/billing/selectors'
import selectBillingUsage from 'ducks/billing/selectors/selectBillingUsage'
import {
  doUpdateCard,
  doUpdateSubscription,
  doFetchBillingData,
  doExternalApprovePendingSubscription,
  doStartTrial,
} from 'ducks/billing/operations'
import { selectProductFeatureUsage } from 'ducks/billing/selectors/features'
import {
  buildLineItemFromPlan,
  buildLineItemsFromPlans,
  calculateSubscriptionFlatTotal,
  calculateSubscriptionUsageTotal,
  isPlanLimitsExceeded,
} from './util'
import {
  FEATURE_INBOX_MAX_CHANNELS,
  FEATURE_INBOX_MAX_MAILBOXES,
  FEATURE_INBOX_MAX_USERS,
} from './featureTypes'
import { INBOX, KB } from './productTypes'
import { selectUsageForProduct } from './selectors/usage'

export const useBilling = () => {
  const dispatch = useDispatch()
  const provider = useSelector(selectBillingProvider)
  const billingCycle = useSelector(selectCurrentBillingCycle)
  const currentInbox = useSelector(selectCurrentInbox)
  const currentKb = useSelector(selectCurrentKb)
  const plans = useSelector(selectPlans)
  const isSubscriptionActive = useSelector(selectIsSubscriptionActive)
  const { loading: isSaving } = useSelector(selectUpdateSubscriptionRequest)
  const isTrial = useSelector(state => selectIsTrial(state, 'inbox'))
  const isFreemium = useSelector(state => selectIsFreemium(state, 'inbox'))
  const primaryCard = useSelector(selectPrimaryCreditCard)
  const isBillingEstimateAvailable = useSelector(
    selectIsBillingEstimateAvailable
  )
  const usage = useSelector(selectBillingUsage)
  const showCardError = useSelector(selectShowCardErrorModal)
  const isUpdatingCard = useSelector(selectIsUpdatingCard)
  const isCanceling = useSelector(selectIsCanceling)
  const hasPlans = useSelector(selectHasPlans)
  const isLoaded = useSelector(selectIsBillingDataLoaded)
  const isLoading = useSelector(selectIsBillingDataLoading)
  const pendingSubscription = useSelector(selectBillingPendingSubscription)
  const providerSettings = useSelector(selectBillingProviderSettings)
  const isBillingLinked = !!useSelector(selectBillingExternalAccountId)
  const externalAccountId = useSelector(selectBillingExternalAccountId)
  const externalSubscriptionId = useSelector(
    selectBillingExternalSubscriptionId
  )
  const hasCompletedTrial = useSelector(selectHasCompletedTrial)
  const startTrialDurationDays = useSelector(
    selectBillingStartTrialDurationDays
  )

  const trialDaysLeft = useSelector(state =>
    selectDaysLeftBeforeExpiring(state, 'inbox')
  )
  const isActiveTrial = isTrial && (trialDaysLeft || 0) > 0

  const hasPendingDowngrade = useSelector(selectHasPendingDowngrade)

  const updateCard = useCallback(
    stripeToken => dispatch(doUpdateCard(stripeToken)),
    [dispatch]
  )

  const updateSubscription = useCallback(
    (form, options) => {
      const { inboxPlanId, kbPlanId, billingCycle: formBillingCycle } = form
      return dispatch(
        doUpdateSubscription(
          {
            inbox: inboxPlanId,
            kb: kbPlanId,
            cycle: formBillingCycle,
          },
          options
        )
      )
    },
    [dispatch]
  )

  const startTrial = useCallback(
    () => {
      return dispatch(doStartTrial('inbox'))
    },
    [dispatch]
  )

  const reloadBilling = useCallback(
    () => {
      return dispatch(doFetchBillingData())
    },
    [dispatch]
  )

  const submitForm = useCallback(formId => {
    document
      .getElementById(formId)
      .dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
  }, [])

  const externalApprovePendingSubscription = useCallback(
    options => dispatch(doExternalApprovePendingSubscription(options)),
    [dispatch]
  )

  const updateCardAndStartSubscription = useCallback(
    async (stripeToken, formId) => {
      await updateCard(stripeToken)
      submitForm(formId)
    },
    [updateCard, submitForm]
  )

  const buildLineItemsFromPlansForBillingCycle = useCallback(
    forBillingCycle =>
      buildLineItemsFromPlans({
        inboxPlanId: currentInbox?.plan?.planId,
        kbPlanId: currentKb?.plan?.planId,
        billingCycle: forBillingCycle,
        plans,
        usage,
        provider,
      }),
    [
      currentInbox?.plan?.planId,
      currentKb?.plan?.planId,
      plans,
      provider,
      usage,
    ]
  )

  const calculateEstimatedTotalBill = useCallback(
    forBillingCycle => {
      const lineItems = buildLineItemsFromPlansForBillingCycle(forBillingCycle)
      const flatTotal = calculateSubscriptionFlatTotal(lineItems) || 0
      const usageTotal = calculateSubscriptionUsageTotal(lineItems) || 0

      return flatTotal + usageTotal
    },
    [buildLineItemsFromPlansForBillingCycle]
  )

  return {
    billingCycle,
    currentInbox,
    currentKb,
    externalAccountId,
    externalApprovePendingSubscription,
    externalSubscriptionId,
    hasCompletedTrial,
    hasPlans,
    isBillingEstimateAvailable,
    isBillingLinked,
    isCanceling,
    isLoaded,
    isLoading,
    isSaving,
    isSubscriptionActive,
    isTrial,
    // Kevin R, hmm we should probably just replace isTrial with the implementation of
    // isActiveTrial. Unfortunately isTrial is used in quite a few places so for now we'll
    // add the 2nd definiction and just start using that moving forward until we can get rid
    // of the old definition
    isActiveTrial,
    isFreemium,
    isUpdatingCard,
    pendingSubscription,
    plans,
    primaryCard,
    provider,
    providerSettings,
    reloadBilling,
    showCardError,
    startTrial,
    startTrialDurationDays,
    submitForm,
    trialDaysLeft,
    updateCard,
    updateCardAndStartSubscription,
    updateSubscription,
    usage,
    calculateEstimatedTotalBill,
    hasPendingDowngrade,
  }
}

export const useProductFeature = (product = INBOX, feature) => {
  const { canUseFeature, featureLimit, currentUsage } = useSelector(state =>
    selectProductFeatureUsage(state, product, feature)
  )

  return {
    canUseFeature,
    featureLimit,
    currentUsage,
  }
}

export const useProductChannelFeature = (isChat = false) => {
  const {
    canUseFeature: channelCanUseFeature,
    featureLimit: channelFeatureLimit,
    currentUsage: channelCurrentUsage,
  } = useProductFeature(INBOX, FEATURE_INBOX_MAX_CHANNELS)

  const {
    canUseFeature: mailboxCanUseFeature,
    featureLimit: mailboxFeatureLimit,
    currentUsage: mailboxCurrentUsage,
  } = useProductFeature(INBOX, FEATURE_INBOX_MAX_MAILBOXES)

  if (isChat || channelFeatureLimit !== 'unlimited') {
    return {
      canUseFeature: channelCanUseFeature,
      featureLimit: channelFeatureLimit,
      currentUsage: channelCurrentUsage,
    }
  }

  return {
    canUseFeature: mailboxCanUseFeature,
    featureLimit: mailboxFeatureLimit,
    currentUsage: mailboxCurrentUsage,
  }
}

export const usePlanChangeInfo = (newPlanId, newBillingCycle, productType) => {
  const {
    currentInbox,
    currentKb,
    billingCycle: currentBillingCycle,
  } = useBilling()

  const usage = useSelector(state => selectUsageForProduct(state, productType))
  const { currentUsage: currentUsageUsers } = useProductFeature(
    productType,
    FEATURE_INBOX_MAX_USERS
  )
  const billingPlans = useSelector(state =>
    selectAvailablePlansByProduct(state, productType)
  )

  const currentPlan = useSelector(state =>
    selectCurrentPlanByProduct(state, productType)
  )?.plan

  const isCurrentPlanLegacy = useSelector(state =>
    selectIsPlanLegacy(state, currentPlan.planId, currentPlan.product)
  )

  const newPlan = useMemo(
    () => billingPlans.find(plan => plan.planId === newPlanId),
    [newPlanId, billingPlans]
  )

  const isNewPlanLegacy = useSelector(state =>
    selectIsPlanLegacy(state, newPlan?.planId, newPlan?.product)
  )

  const billingEstimate = useSelector(selectBillingEstimateForPlanChange)

  const showKbPlans = useSelector(selectShowKbPlans)

  const newBillingCycleOrDefault = newBillingCycle || currentBillingCycle
  const isCycleChange = currentBillingCycle !== newBillingCycleOrDefault

  const isUpgradingToAnnual =
    newBillingCycleOrDefault === 'annual' && currentBillingCycle === 'monthly'

  const currentPlanLineItem = buildLineItemFromPlan(
    currentPlan,
    currentBillingCycle,
    currentUsageUsers
  )

  const newPlanLineItem = buildLineItemFromPlan(
    newPlan,
    newBillingCycleOrDefault,
    currentUsageUsers
  )

  const currentPlanDisplayName = `${isCurrentPlanLegacy ? 'Legacy' : ''} ${
    currentPlan.name
  }`.trim()

  const newPlanDisplayName = `${isNewPlanLegacy ? 'Legacy' : ''} ${
    newPlan?.name
  }`.trim()

  const otherProductType = productType === INBOX ? KB : INBOX
  const includeOtherProductInBreakdown =
    isCycleChange &&
    (otherProductType === INBOX || (otherProductType === KB && showKbPlans))

  const currentOtherProductPlanId = useMemo(
    () => {
      const product = otherProductType === INBOX ? currentInbox : currentKb

      return product?.plan?.planId
    },
    [otherProductType, currentInbox, currentKb]
  )

  return {
    current: {
      displayName: currentPlanDisplayName,
      plan: currentPlan,
      isLegacy: isCurrentPlanLegacy,
      lineItem: currentPlanLineItem,
      billingCycle: currentBillingCycle,
      isPlanLimitsExceeded: isPlanLimitsExceeded(currentPlan, usage),
    },
    new: {
      displayName: newPlanDisplayName,
      plan: newPlan,
      isLegacy: isNewPlanLegacy,
      lineItem: newPlanLineItem,
      billingCycle: newBillingCycleOrDefault,
      isPlanLimitsExceeded: isPlanLimitsExceeded(newPlan, usage),
    },
    isUpgradingToAnnual,
    isCycleChange,
    billingEstimate,
    currentUsageUsers,
    otherProductType,
    includeOtherProductInBreakdown,
    otherProduct: {
      productType: otherProductType,
      billingCycle: newBillingCycle,
      planId: currentOtherProductPlanId,
    },
  }
}
